import '@/common/application/lib/dynamicImportErrorListener';
import '@fontsource/inter/latin-400.css';
import '@fontsource/inter/latin-500.css';
import '@fontsource/inter/latin-600.css';
import '@fontsource/inter/latin-700.css';
import '@fontsource/lato/latin-400.css';
import '@fontsource/lato/latin-700.css';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { ApolloProvider } from '@apollo/client';
import App from '@/common/presentation/App';
import { CssBaseline } from '@mui/material';
import { LicenseInfo } from '@mui/x-license';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { QueryClientProvider } from '@tanstack/react-query';
import SnackbarProvider from '@/common/presentation/SnackbarProvider';
import { ThemeProvider } from '@mui/material/styles';
import apolloClient from '@/common/infrastructure/gql/apollo/client';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import authServices from '@/auth/main/DependencyInjection';
import { createRoot } from 'react-dom/client';
import { initializeAxiosInstance } from '@/libs/api-rest';
import { initializeUserManagementAxiosInstance } from '@/libs/api-user-mgt/axios-instance';
import { queryClient } from '@/common/infrastructure/config/queryClient';
import reportWebVitals from '@/common/infrastructure/services/reportWebVitals';
import { BACKGROUND_COLORS, CERTPERM_COLORS, TEXT_COLORS } from '@/common/presentation/constants/colors';
import { themeOptions } from '@/common/presentation/themeOptions';
import type { ComponentType, PropsWithChildren } from 'react';

import('@/common/infrastructure/config/sentry');

// Preconnect at runtime to critical backends (helps reduce connection setup time)
function addPreconnect(href: string, crossOrigin = false) {
  if (!href) return;
  try {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = href;
    if (crossOrigin) link.crossOrigin = '';
    document.head.appendChild(link);

    const dns = document.createElement('link');
    dns.rel = 'dns-prefetch';
    dns.href = href;
    document.head.appendChild(dns);
  } catch (error) {
    console.error(error);
  }
}

const NoopProvider: ComponentType<PropsWithChildren> = (props) => <>{props.children}</>;

/**
 * UM REST must not fall back to localhost in production — that can send real tokens to unintended hosts.
 * In dev only, localhost mock-api is allowed when env vars are unset.
 */
function resolveUserManagementApiBase(): string {
  const configuredOrigin = import.meta.env.VITE_UM_API_BASE_URL?.trim() || import.meta.env.VITE_APP_API_URL?.trim() || '';

  // `npm run dev` uses `--mode localhost`: keep UM on the Vite origin and forward via vite.config proxy.
  // Avoids browser TLS errors when `VITE_UM_API_BASE_URL` points at a local HTTPS port (e.g. :55272).
  if (import.meta.env.DEV && import.meta.env.MODE === 'localhost' && configuredOrigin) {
    return typeof window !== 'undefined' ? window.location.origin : configuredOrigin;
  }

  if (configuredOrigin) return configuredOrigin;
  if (import.meta.env.DEV) return 'http://localhost:3010/';
  throw new Error(
    'User Management API base URL is not configured. Set VITE_UM_API_BASE_URL or VITE_APP_API_URL for this build so auth tokens are not sent to unintended endpoints.'
  );
}

// Suppress browser extension errors that don't affect app functionality
// This error occurs when browser extensions (React DevTools, Redux DevTools, etc.)
// try to communicate with the page but the message channel closes prematurely
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    const errorMessage = event.message || '';
    if (
      errorMessage.includes('message channel closed') ||
      errorMessage.includes('asynchronous response') ||
      errorMessage.includes('listener indicated')
    ) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    const errorMessage = event.reason?.message || String(event.reason || '');
    if (
      errorMessage.includes('message channel closed') ||
      errorMessage.includes('asynchronous response') ||
      errorMessage.includes('listener indicated')
    ) {
      event.preventDefault();
      return false;
    }
  });
}

(async () => {
  const container = document.getElementById('root');

  if (!container) {
    console.error('Application root element not found.');
    return;
  }

  const root = createRoot(container);
  const isLighthouseRun = import.meta.env.VITE_LH === '1';

  try {
    initializeAxiosInstance(
      {
        getToken: () => authServices.tokenService.getToken()
      },
      import.meta.env.VITE_API_BASE_URL
    );

    const userManagementApiBase = resolveUserManagementApiBase();
    initializeUserManagementAxiosInstance({ getToken: () => authServices.tokenService.getToken() }, userManagementApiBase);

    addPreconnect(import.meta.env.VITE_API_BASE_URL || '');
    addPreconnect(userManagementApiBase.replace(/\/$/, '') || '');
    addPreconnect(import.meta.env.VITE_GRAPHQL_URL || '');

    LicenseInfo.setLicenseKey(import.meta.env.VITE_MUIX_LICENSE_KEY);

    root.render(<div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading…</div>);

    let LDProvider: ComponentType<PropsWithChildren> = NoopProvider;

    if (!isLighthouseRun) {
      const ldStreamingEnabled = import.meta.env.VITE_LAUNCHDARKLY_STREAMING !== 'false';
      const LaunchDarklyProvider = await asyncWithLDProvider({
        clientSideID: import.meta.env.VITE_LAUNCHDARKLY_ENV,
        context: {
          kind: 'user',
          key: 'anonymous',
          anonymous: true
        },
        options: {
          streaming: ldStreamingEnabled
        },
        reactOptions: {
          useCamelCaseFlagKeys: false
        },
        timeout: 1
      });
      LDProvider = LaunchDarklyProvider as ComponentType<PropsWithChildren>;
    }

    root.render(
      <QueryClientProvider client={queryClient}>
        <LDProvider>
          <ApolloProvider client={apolloClient}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <ThemeProvider theme={themeOptions}>
                <CssBaseline />
                <SnackbarProvider>
                  <App />
                </SnackbarProvider>
              </ThemeProvider>
            </LocalizationProvider>
          </ApolloProvider>
        </LDProvider>
      </QueryClientProvider>
    );
  } catch (error) {
    console.error('Failed to initialize application shell.', error);
    root.render(
      <div
        role="alert"
        style={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'Inter, sans-serif',
          textAlign: 'center',
          padding: '24px'
        }}
      >
        <p style={{ fontSize: '1.125rem', margin: 0 }}>We couldn&apos;t start the application.</p>
        <p style={{ margin: 0, color: TEXT_COLORS.MUTED }}>Please refresh the page or try again later.</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            border: 'none',
            borderRadius: '6px',
            padding: '10px 20px',
            backgroundColor: CERTPERM_COLORS.PRIMARY_LIGHT,
            color: BACKGROUND_COLORS.WHITE,
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Retry
        </button>
      </div>
    );
    return;
  } finally {
    reportWebVitals();
  }
})();

// Export components for use in other files
export { App, reportWebVitals };
